import React, { useState } from 'react';
import { Database } from 'lucide-react';
import FormModal from './FormModal';
import type { CreateFrameworkDTO, UpdateFrameworkDTO, FrameworkWithVersions } from '@domains/compliance/definition/frameworkVersions';

interface FrameworkModalProps {
  onClose: () => void;
  onSubmit: (framework: CreateFrameworkDTO | UpdateFrameworkDTO, selectedFramework?: FrameworkWithVersions) => Promise<void> | void;
  selectedFramework: FrameworkWithVersions | null;
}

export const FrameworkModal: React.FC<FrameworkModalProps> = ({
  onClose,
  onSubmit,
  selectedFramework,
}) => {
  const [framework, setFramework] = useState<CreateFrameworkDTO | UpdateFrameworkDTO>(
    selectedFramework || {
      name: '',
      authority: '',
      description: '',
    }
  );

  const handleSubmit = async (data: any) => {
    try {
      await onSubmit({ ...framework, ...data }, selectedFramework || undefined);
      onClose();
    } catch {
      // error already handled by parent handler; keep modal open
    }
  };

  const isCreate = !selectedFramework?.id;

  return (
    <FormModal
      title={isCreate ? 'Create Framework' : 'Edit Framework'}
      subtitle={isCreate ? 'Define the compliance standard name, authority, and description' : 'Modify framework details'}
      icon={<Database className="text-white" size={24} />}
      onSubmit={handleSubmit}
      onCancel={onClose}
      submitText={isCreate ? 'Create Framework' : 'Update Framework'}
      cancelText="Cancel"
      size="large"
    >
      <div className="space-y-5">
        <div>
          <label htmlFor="name" className="block text-sm font-semibold text-slate-700 mb-1.5">
            Framework Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            required
            value={framework.name}
            onChange={(e) => setFramework({ ...framework, name: e.target.value })}
            className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm"
            placeholder="e.g., ISO 27001, SOC 2, GDPR"
          />
          <p className="text-xs text-slate-400 mt-1.5">The official name of the compliance standard or regulation</p>
        </div>

        <div>
          <label htmlFor="authority" className="block text-sm font-semibold text-slate-700 mb-1.5">
            Authority
          </label>
          <input
            type="text"
            id="authority"
            name="authority"
            value={framework.authority || ''}
            onChange={(e) => setFramework({ ...framework, authority: e.target.value })}
            className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm"
            placeholder="e.g., ISO/IEC, AICPA, European Union"
          />
          <p className="text-xs text-slate-400 mt-1.5">The body or organisation that publishes this standard</p>
        </div>

        <div>
          <label htmlFor="description" className="block text-sm font-semibold text-slate-700 mb-1.5">
            Description
          </label>
          <textarea
            id="description"
            name="description"
            value={framework.description || ''}
            onChange={(e) => setFramework({ ...framework, description: e.target.value })}
            rows={4}
            className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm"
            placeholder="Describe the purpose and scope of this framework..."
          />
        </div>
      </div>
    </FormModal>
  );
};
